import React, { useState, useEffect, useMemo, useCallback } from "react";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import api from "../../utils/api";
import { apiUrls } from "../../utils/urls";
import { useProfile } from "../../context/ProfileContext";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import useDisclosure from "../../hooks/ui/useDisclosure";
import ProfilePictureUpload from "../ProfilePictureUpload/ProfilePictureUpload";
import RadioGroup from "../RadioGroup";
import { useACTprepTheme } from "../../context/ACTprepContextPackage";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { formatPhoneNumber } from "../../utils/formatting";
import {
  Add,
  Check,
  CheckCircleOutline,
  Edit,
  Refresh,
} from "@mui/icons-material";
import { showSuccessSnackbar } from "../../utils/feedback";
import VerifyEmailDialog from "../VerifyEmailDialog";
import VerifyPhoneNumberDialog from "../VerifyPhoneNumberDialog";
import { useConfirm } from "material-ui-confirm";
import { LoadingButton } from "@mui/lab";
import AdminProfileSettings from "../AdminProfileSettings";
import EditPhoneDialog from "../EditPhoneDialog";

const ProfileSettings = () => {
  const confirm = useConfirm();
  const { profile, refetchUser } = useProfile();
  const { themeFlag, toggleTheme } = useACTprepTheme();
  const {
    isOpen: isUploadOpen,
    onOpen: onUploadOpen,
    onClose: onUploadClose,
  } = useDisclosure();

  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [loadingNewEmail, setLoadingNewEmail] = useState(false);
  const changeEmailDisclosure = useDisclosure();
  const addPhoneNumberDisclosure = useDisclosure();
  const verifyPhoneNumberDisclosure = useDisclosure();
  const verifyEmailDisclosure = useDisclosure();
  const [firstName, setFirstName] = useState(profile.user?.first_name || "");
  const [lastName, setLastName] = useState(profile.user?.last_name || "");

  // Timezone state & browser detection
  const browserTimeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    []
  );
  const [timezone, setTimezone] = useState(profile.timezone || browserTimeZone);
  const timeZones = useMemo(() => {
    if (Intl.supportedValuesOf) {
      return Intl.supportedValuesOf("timeZone");
    }
    return [browserTimeZone];
  }, [browserTimeZone]);

  useEffect(() => {
    setFirstName(profile.user?.first_name || "");
    setLastName(profile.user?.last_name || "");
    setTimezone(profile.timezone || browserTimeZone);
  }, [profile, browserTimeZone]);

  const reloadProfile = async () => {
    await refetchUser();
  };

  const handleSave = useCallback(async () => {
    try {
      await api.post(apiUrls.users.profile(), {
        first_name: firstName,
        last_name: lastName,
        timezone,
      });
      setEditMode(false);
      showSuccessSnackbar("Profile Saved!");
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  }, [firstName, lastName, timezone]);

  const handleSubmitPhoneNumber = async (phoneNumber, allowTexting) => {
    event.preventDefault();
    const { data } = await api.post(apiUrls.users.profile(), {
      phone_number: phoneNumber?.replace(" ", ""),
      allow_texts: allowTexting,
    });
    if (!data?.success) {
      const reason = data?.reason || "We experienced an error.";
      setError(reason);
      return;
    }
    refetchUser();
    addPhoneNumberDisclosure.onClose();
    showSuccessSnackbar("Phone number updated!");
  };

  const handleSubmitEmail = async (event) => {
    event.preventDefault();
    if (!newEmail) return;
    setLoadingNewEmail(true);
    const { data } = await api.post(apiUrls.users.changeEmail(), {
      new_email: newEmail,
      redirect_url: window.location.href,
    });
    setLoadingNewEmail(false);
    if (!data?.success) {
      const reason = data?.reason || "We experienced an error.";
      setError(reason);
      return;
    }
    confirm({
      title: "Email Change Link Sent",
      content: (
        <>
          <Typography variant="body1">
            You have been sent an email change link to your new email:{" "}
            <strong>{newEmail}</strong>.
          </Typography>
          <Typography variant="body1" mt={2}>
            Visit the link to complete the email change process.
          </Typography>
        </>
      ),
      hideCancelButton: true,
    });
    changeEmailDisclosure.onClose();
  };

  useEffect(() => {
    if (timezone != profile?.timezone) {
      handleSave().then(() => refetchUser());
    }
  }, [timezone, profile?.timezone, handleSave, refetchUser]);

  return (
    <>
      <Box sx={{ position: "relative", mb: 2 }}>
        <Grid container sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Box sx={{ width: "5rem", height: "5rem" }}>
              <Avatar
                alt="Profile Picture"
                src={profile.profile_pic_url}
                sx={{ width: "100%", height: "100%" }}
              >
                {(profile?.user.first_name[0] || "") +
                  (profile?.user.last_name[0] || "")}
              </Avatar>
              <IconButton
                onClick={onUploadOpen}
                sx={{
                  position: "relative",
                  top: "-100%",
                  height: "100%",
                  width: "100%",
                  transition: "background-color 500ms",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.5)" },
                  "& svg": { visibility: "hidden" },
                  "&:hover svg": { visibility: "visible" },
                }}
              >
                <Edit color="secondary" />
              </IconButton>
            </Box>
          </Grid>

          <Grid container>
            {editMode ? (
              <>
                <TextField
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  variant="standard"
                  margin="normal"
                  sx={{ mr: 2 }}
                />
                <TextField
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  variant="standard"
                  margin="normal"
                />
              </>
            ) : (
              <Typography variant="h6">
                {profile.user?.first_name} {profile.user?.last_name}
              </Typography>
            )}

            {/* Email Section */}
            <Grid
              container
              sx={(theme) => ({
                backgroundColor: theme.palette.background.secondary,
                border: `1px solid ${theme.palette.divider}`,
                width: "100%",
                mb: 0.5,
                p: 1,
                borderRadius: ".25rem",
              })}
            >
              <Grid size={6}>
                <Stack direction="row" flexWrap="wrap">
                  <Typography variant="body2" mr={0.5}>
                    Email:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {profile.user?.email}
                  </Typography>
                </Stack>
              </Grid>
              <Grid size={6}>
                <Stack
                  direction="row"
                  gap={1}
                  flexWrap="wrap"
                  sx={{ justifyContent: "flex-end" }}
                >
                  <Chip
                    size="small"
                    label="Change Email"
                    onClick={changeEmailDisclosure.onOpen}
                  />
                  {profile.email_verified ? (
                    <Chip
                      size="small"
                      label="Verified"
                      icon={<Check />}
                      color="success"
                    />
                  ) : (
                    <Badge
                      badgeContent={1}
                      anchorOrigin={{ vertical: "top", horizontal: "left" }}
                      color="error"
                    >
                      <Chip
                        size="small"
                        label="Verify"
                        icon={<CheckCircleOutline />}
                        onClick={verifyEmailDisclosure.onOpen}
                      />
                    </Badge>
                  )}
                </Stack>
              </Grid>
            </Grid>

            {/* Phone Section */}
            <Grid
              container
              sx={(theme) => ({
                backgroundColor: theme.palette.background.secondary,
                border: `1px solid ${theme.palette.divider}`,
                width: "100%",
                mb: 0.5,
                p: 1,
                borderRadius: ".25rem",
              })}
            >
              <Grid size={6}>
                <Stack direction="row" flexWrap="wrap">
                  <Typography variant="body2" mr={0.5}>
                    Phone:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatPhoneNumber(profile.phone_number) || "None"}
                  </Typography>
                </Stack>
              </Grid>
              <Grid size={6}>
                <Stack
                  direction="row"
                  gap={1}
                  flexWrap="wrap"
                  sx={{ justifyContent: "flex-end" }}
                >
                  {profile.phone_number ? (
                    <>
                      <Chip
                        size="small"
                        label="Edit"
                        icon={<Edit />}
                        onClick={addPhoneNumberDisclosure.onOpen}
                      />
                      {profile.phone_number_verified ? (
                        <Chip
                          size="small"
                          label="Verified"
                          icon={<Check />}
                          color="success"
                        />
                      ) : (
                        <Badge
                          badgeContent={1}
                          anchorOrigin={{ vertical: "top", horizontal: "left" }}
                          color="error"
                        >
                          <Chip
                            size="small"
                            label="Verify"
                            icon={<CheckCircleOutline />}
                            onClick={verifyPhoneNumberDisclosure.onOpen}
                          />
                        </Badge>
                      )}
                    </>
                  ) : (
                    <Chip
                      size="small"
                      label="Add"
                      icon={<Add />}
                      onClick={addPhoneNumberDisclosure.onOpen}
                    />
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Top-right refresh & edit buttons */}
        <Stack
          direction="row-reverse"
          flexWrap="wrap"
          justifyContent="center"
          sx={{ position: "absolute", top: 0, right: 0, maxWidth: "50%" }}
        >
          <Chip
            onClick={reloadProfile}
            label="Refresh"
            icon={<Refresh />}
            size="small"
            sx={{ m: 0.5 }}
          />
          <Chip
            onClick={editMode ? handleSave : () => setEditMode(true)}
            label={editMode ? "Save Name" : "Edit Name"}
            icon={editMode ? <Check /> : <Edit />}
            size="small"
            sx={{ m: 0.5 }}
          />
        </Stack>
        {/* Admin Settings if applicable */}
        {profile?.is_admin && (
          <Box mb={2}>
            <AdminProfileSettings adminProfile={profile.admin} />
          </Box>
        )}
        {/* Timezone Selector: inserted before Display Mode */}
        <Box sx={{ mb: 2 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight="bold">
              Time Zone
            </Typography>
            <TextField
              select
              label="Select Timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              variant="standard"
            >
              {timeZones.map((tz) => (
                <MenuItem key={tz} value={tz}>
                  {tz}
                </MenuItem>
              ))}
            </TextField>
            {browserTimeZone !== timezone && (
              <Chip
                size="small"
                color="error"
                onClick={() => setTimezone(browserTimeZone)}
                label={
                  <Box>
                    Use current timezone:{" "}
                    <Box component={"span"} fontWeight={"bold"}>
                      {browserTimeZone}
                    </Box>
                  </Box>
                }
                sx={{ width: "fit-content" }}
                component={"div"}
              />
            )}
          </Stack>
        </Box>

        {/* Display Mode */}
        <Typography variant="subtitle1" fontWeight="bold">
          Display Mode
        </Typography>
        <RadioGroup
          value={themeFlag}
          setValue={(newVal) => toggleTheme(newVal)}
          options={[
            { label: "Match system setting", value: "auto" },
            { label: "Dark", value: "dark" },
            { label: "Light", value: "light" },
          ]}
        />

        <ProfilePictureUpload isOpen={isUploadOpen} onClose={onUploadClose} />
      </Box>

      {/* Add/Edit Phone Dialog */}
      <EditPhoneDialog
        open={addPhoneNumberDisclosure.isOpen}
        onClose={addPhoneNumberDisclosure.onClose}
        onSubmit={handleSubmitPhoneNumber}
        initialPhone={profile.phone_number}
        hasExisting={!!profile.phone_number}
        allowTexting={profile.allow_texts}
      />

      {/* Change Email Dialog */}
      <Dialog
        {...changeEmailDisclosure.getDisclosureProps()}
        maxWidth="xs"
        fullWidth
      >
        <Box component="form" onSubmit={handleSubmitEmail}>
          <DialogTitle>Change Email</DialogTitle>
          <DialogContent>
            <Typography variant="subtitle2">
              Current Email: <strong>{profile.user.email}</strong>
            </Typography>
            <TextField
              label="New Email"
              variant="standard"
              type="email"
              fullWidth
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={changeEmailDisclosure.onClose}
              disabled={loadingNewEmail}
            >
              Close
            </Button>
            <LoadingButton
              type="submit"
              disabled={!newEmail || loadingNewEmail}
              loading={loadingNewEmail}
            >
              Send Link
            </LoadingButton>
          </DialogActions>
        </Box>
      </Dialog>

      <VerifyEmailDialog {...verifyEmailDisclosure.getDisclosureProps()} />
      <VerifyPhoneNumberDialog
        {...verifyPhoneNumberDisclosure.getDisclosureProps()}
      />
    </>
  );
};

export default ProfileSettings;
